import { useEffect, useState } from "react";

/**
 * Debug panel visibility.
 *
 * BUILD-TIME GATE (mainnet safety): the dev console exposes raw instruction
 * buttons (init_season, latch_milestone, fund_vault, pause, …) with no
 * confirmation. It must NOT be reachable on the public mainnet site. It is
 * available only when:
 *   - running under `import.meta.env.DEV`, OR
 *   - the prod build was explicitly built with `VITE_ENABLE_DEV_CONSOLE=1`.
 * A default production build renders nothing — no `?debug=1`, no backtick.
 * For mainnet operations use the admin-gated `/ops` dashboard instead.
 *
 * When available, enabled by `?debug=1` or the backtick (`) toggle.
 */
export function useDebugVisible(): boolean {
  const available =
    import.meta.env.DEV ||
    (import.meta.env.VITE_ENABLE_DEV_CONSOLE as string | undefined) === "1";

  const [enabled, setEnabled] = useState<boolean>(() => {
    if (!available) return false;
    if (typeof window === "undefined") return false;
    if (import.meta.env.DEV) return true;
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "1";
  });
  const [shown, setShown] = useState<boolean>(false);

  useEffect(() => {
    if (!available) return;
    if (typeof window === "undefined") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "`") return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) {
          return;
        }
      }
      setShown((v) => !v);
      // Force-enable on first toggle so an operator on an opted-in build can
      // open the panel without the query param. Only reachable when `available`.
      setEnabled(true);
      e.preventDefault();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [available]);

  if (!available) return false;
  return enabled && shown;
}
